import React, { useEffect, useState } from 'react'
import { Button, Checkbox, Message } from 'semantic-ui-react'
import styled from 'styled-components'
import { saveDealProposal } from 'utility/services/crm'
import { toApiError } from 'utility/crm/api-error'
import { formatMoney } from 'utility/crm/dates'
import { initialQuote, quoteBody, quoteLines } from 'utility/crm/proposal'
import { ALL_CRM_SCOPES, invalidateCrm, useCrmLookups, useCrmToast, useProposalQuote } from 'hooks/crm'
import { CrmModal } from './CrmOverlay'
import { Muted, Row } from './ui'

const PlanCard = styled.button`
    display: block;
    width: 100%;
    text-align: left;
    padding: 10px 12px;
    margin-bottom: 8px;
    border-radius: 8px;
    cursor: pointer;
    color: inherit;
    background: ${({ $active }) => ($active ? 'rgba(37,99,235,.15)' : 'transparent')};
    border: 2px solid ${({ $active }) => ($active ? '#2563eb' : 'rgba(128,128,128,.35)')};
`

const Breakdown = styled.div`
    border: 1px solid rgba(128, 128, 128, 0.35);
    border-radius: 8px;
    padding: 12px 14px;
    margin-top: 12px;
    opacity: ${({ $stale }) => ($stale ? 0.6 : 1)};
    .line {
        display: flex;
        justify-content: space-between;
        padding: 3px 0;
    }
    .line.strong {
        font-weight: 700;
        font-size: 1.1rem;
        border-top: 1px solid rgba(128, 128, 128, 0.35);
        margin-top: 6px;
        padding-top: 8px;
    }
`

const numberField = { width: '100%', padding: 8 }

/**
 * Build a proposal: the price updates as you type (quote only, nothing saved). Saving sets the deal
 * amount to the annual value and moves it to Proposal Sent.
 * @param {{ deal: import('utility/crm/types').CrmDeal|null, onClose: () => void,
 *   onPreview: (deal: import('utility/crm/types').CrmDeal, proposal: import('utility/crm/types').CrmProposal) => void }} props
 */
export const ProposalDialog = ({ deal, onClose, onPreview }) => {
    const { lookups } = useCrmLookups()
    const { successToast } = useCrmToast()
    const [quote, setQuote] = useState(() => initialQuote(deal?.Proposal))
    const [saving, setSaving] = useState(false)
    const [saveError, setSaveError] = useState(null)
    const live = useProposalQuote(quote)

    // Start over each time the dialog opens for a deal.
    useEffect(() => {
        if (deal) {
            setQuote(initialQuote(deal.Proposal))
            setSaveError(null)
        }
    }, [deal?.DealId]) // eslint-disable-line

    const set = (key, value) => setQuote(q => ({ ...q, [key]: value }))
    const plans = lookups?.Plans || []
    const { proposal } = live
    const canSave = live.ready && Boolean(proposal) && !live.stale && !live.error && !saving
    const plan = plans.find(p => p.Id === quote.Plan)
    const annualPct = lookups ? Math.round((lookups.AnnualDiscount || 0) * 100) : 0

    const save = async () => {
        setSaving(true)
        setSaveError(null)
        try {
            await saveDealProposal(deal.DealId, quoteBody(quote))
            successToast('Proposal saved. Deal moved to Proposal Sent')
            invalidateCrm(...ALL_CRM_SCOPES)
            onClose()
        } catch (err) {
            setSaveError(toApiError(err).message)
            invalidateCrm('deals', 'forecast')
        } finally {
            setSaving(false)
        }
    }

    return (
        <CrmModal
            open={Boolean(deal)}
            onClose={saving ? () => {} : onClose}
            title={deal ? `Proposal · ${deal.AccountName}` : 'Proposal'}
            width={620}
            footer={
                <>
                    <Button type="button" onClick={() => onPreview(deal, proposal)} disabled={!proposal || !live.ready} style={{ marginRight: 'auto' }}>
                        Preview printable proposal
                    </Button>
                    <Button type="button" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="button" primary onClick={save} loading={saving} disabled={!canSave}>
                        Save to deal &amp; mark Proposal Sent
                    </Button>
                </>
            }
        >
            {saveError ? (
                <Message negative role="alert">
                    {saveError}
                </Message>
            ) : null}

            <div role="radiogroup" aria-label="Plan">
                {plans.map(p => (
                    <PlanCard key={p.Id} type="button" role="radio" aria-checked={quote.Plan === p.Id} $active={quote.Plan === p.Id} onClick={() => set('Plan', p.Id)}>
                        <strong>{p.Name}</strong>
                        <div>
                            <Muted>
                                ${p.SitePrice}/site/month · {p.Description}
                            </Muted>
                        </div>
                    </PlanCard>
                ))}
            </div>

            <Row $gap={12} $wrap style={{ alignItems: 'flex-start', marginTop: 8 }}>
                <div style={{ flex: 1, minWidth: 120 }}>
                    <label htmlFor="crm-quote-sites" style={{ fontWeight: 600 }}>
                        Sites
                    </label>
                    <input id="crm-quote-sites" type="number" min="1" step="1" value={quote.Sites} onChange={e => set('Sites', e.target.value)} style={numberField} />
                </div>
                <div style={{ flex: 1, minWidth: 120 }}>
                    <label htmlFor="crm-quote-users" style={{ fontWeight: 600 }}>
                        Users
                    </label>
                    <input id="crm-quote-users" type="number" min="1" step="1" value={quote.Users} onChange={e => set('Users', e.target.value)} style={numberField} />
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                    <label htmlFor="crm-quote-billing" style={{ fontWeight: 600 }}>
                        Billing
                    </label>
                    <select id="crm-quote-billing" value={quote.Billing} onChange={e => set('Billing', e.target.value)} style={numberField}>
                        <option value="annual">Annual ({annualPct}% off)</option>
                        <option value="monthly">Monthly</option>
                    </select>
                </div>
            </Row>
            <div style={{ marginTop: 10 }}>
                <Checkbox label="Include onboarding & template setup" checked={quote.Onboarding} onChange={(_, d) => set('Onboarding', Boolean(d.checked))} />
            </div>

            {!live.ready ? (
                <Message warning role="status" style={{ marginTop: 12 }}>
                    Enter at least 1 site and 1 user to see the price.
                </Message>
            ) : null}
            {live.error ? (
                <Message negative role="alert" style={{ marginTop: 12 }}>
                    {live.error.message}
                </Message>
            ) : null}

            {proposal && live.ready ? (
                <Breakdown $stale={live.stale} aria-live="polite" aria-busy={live.stale}>
                    {quoteLines(proposal, plan, lookups?.ExtraUserPrice || 0).map(line => (
                        <div key={line.label} className={`line${line.strong ? ' strong' : ''}`}>
                            <span>{line.label}</span>
                            <span>{line.amount < 0 ? `−${formatMoney(-line.amount)}` : formatMoney(line.amount)}</span>
                        </div>
                    ))}
                    {live.stale ? <Muted>Updating price…</Muted> : null}
                </Breakdown>
            ) : null}
        </CrmModal>
    )
}
